import React, { useRef } from 'react';
import { FocusScope, OverlayContainer, useOverlay } from 'react-aria';
import { Transition } from 'react-transition-group';
import { ListBoxData } from 'components/ListBox';
import Overlay from 'components/Overlay';
import { PopoverContainer, PopoverContainerProps } from 'components/Popover';
import { SELECT_MENU_TRANSITION_DURATION_MILLIS } from './constants';
import { SelectMenuContentsProps } from './SelectMenuContents';
import SelectMenuContentsContainer from './SelectMenuContentsContainer';
import * as S from './styles';

export type SelectMenuProps<T extends ListBoxData> = Pick<
  PopoverContainerProps,
  'triggerElement' | 'preventOverflow'
> &
  Pick<SelectMenuContentsProps<T>, 'footer' | 'menuProps' | 'state'>;

function SelectMenu<T extends ListBoxData>({
  footer,
  menuProps,
  preventOverflow,
  state,
  triggerElement,
}: SelectMenuProps<T>): React.ReactElement | null {
  const contentsContainerRef = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);

  const { overlayProps } = useOverlay(
    {
      isOpen: state.isOpen,
      onClose: state.close,
      isDismissable: true,
      shouldCloseOnBlur: true,
    },
    contentsContainerRef,
  );

  return (
    // Transition animates the popover in/out
    <Transition
      in={state.isOpen}
      timeout={SELECT_MENU_TRANSITION_DURATION_MILLIS}
      nodeRef={contentsRef}
      mountOnEnter
      unmountOnExit
    >
      {(status) => (
        // OverlayContainer renders the menu in a portal
        <OverlayContainer>
          {/* PopoverContainer is responsible for positioning the popover */}
          <PopoverContainer
            isOpen
            offset={10}
            placement="bottom"
            preventOverflow={preventOverflow}
            triggerElement={triggerElement}
            width="target"
          >
            {/* Ovelay blocks scrolling while the popover is visible */}
            <Overlay preventScrolling>
              {/* SelectMenuContentsContainer is used as a dedicated element for the
              PopoverConainer to use for positioning.  This is so the popover positioning
              doesn't conflict with transitions and animations.  positioning and animations
              both often use transforms and positional values */}
              <SelectMenuContentsContainer ref={contentsContainerRef}>
                <FocusScope contain restoreFocus autoFocus>
                  {/* the Contents component holds the actual select menu contents
                    and is the component that gets animated by the Transition */}
                  <S.Contents
                    {...overlayProps}
                    {...{ menuProps, state }}
                    ref={contentsRef}
                    $transitionStatus={status}
                    footer={footer}
                    scrollerClassName="select-menu-scroller"
                  />
                </FocusScope>
              </SelectMenuContentsContainer>
            </Overlay>
          </PopoverContainer>
        </OverlayContainer>
      )}
    </Transition>
  );
}

export default SelectMenu;
